import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Modal, Form, Select, Input, Upload, Button, message, Spin, Row, Col, DatePicker, Tag, Typography, Switch, Descriptions, Space, Image,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { treatmentApi } from '../../../api/treatment.api';
import { clinicApi } from '../../../api/clinic.api';
import { checkupApi } from '../../../api/checkup.api';
import { getApiErrorMessage } from '../../../utils/helpers';

const { Text } = Typography;

const HEALTH_STATUS_OPTIONS = [
  { value: 'clear', label: 'Clear' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'needsReview', label: 'Needs Review' },
];

const BRUSHING_HABIT_OPTIONS = [
  { value: 'good', label: 'Good' },
  { value: 'moderate', label: 'Average' },
  { value: 'needsReview', label: 'Need Improvement' },
];

const ORAL_HYGIENE_LABELS = { good: 'Good', fair: 'Fair', poor: 'Poor' };
const JAW_DEVELOPMENT_LABELS = { good: 'Good', to_be_assigned: 'To Be Assisted' };
const JAW_WIDTH_LABELS = { wide: 'Wide', narrow: 'Narrow' };
const TONGUE_MOVEMENT_LABELS = { good: 'Good', dysfunction: 'Dysfunction', restricted: 'Restricted' };
const yesNo = (v) => (v ? 'Yes' : 'No');

const DEFINITIVE_TREATMENT_OPTIONS = ['Naturally align teeth – Myoline', 'Pulpectomy with full coverage crowns', 'Not applicable'];
const SEMI_PERMANENT_TREATMENT_OPTIONS = ['Braces and Aligners', 'SDF and Filling', 'Not applicable'];
const TEMPORARY_TREATMENT_OPTIONS = ['Wait and Watch', 'Fluoride Varnish', 'Not applicable'];
const PREVENTIVE_TREATMENT_OPTIONS = ['Pit and Fissure Sealant', 'Fluoride Varnish', 'Not applicable'];

const toSelectOptions = (values) => values.map((value) => ({ value, label: value }));

const MAX_IMAGES = 7;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB per image

const StatusToggle = ({ value, onChange }) => (
  <Switch
    checked={value === 'published'}
    onChange={(checked) => onChange(checked ? 'published' : 'draft')}
    checkedChildren="Published"
    unCheckedChildren="Draft"
    style={{ minWidth: 100 }}
  />
);

const TreatmentModal = ({
  open,
  onClose,
  eventId,
  childId,
  childName,
  eventName,
  defaultClinic,
  onSuccess,
  editingRecord = null,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageList, setImageList] = useState([]);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [imageDragIndex, setImageDragIndex] = useState(null);
  const imageCountRef = useRef(0);

  const { data: clinicsData, isLoading: clinicsLoading } = useQuery({
    queryKey: ['clinics-active'],
    queryFn: clinicApi.getActive,
    enabled: open,
  });

  const { data: checkupData, isLoading: checkupLoading } = useQuery({
    queryKey: ['checkup-observations', eventId, childId],
    queryFn: () => checkupApi.getAll({ event: eventId, child: childId }),
    enabled: open && !!eventId && !!childId,
  });

  const checkupRecord = checkupData?.data?.[0];

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setImageList([]);
      imageCountRef.current = 0;
      return;
    }

    setImageList((editingRecord?.images || []).map((imagePath, idx) => ({
      uid: `existing-${idx}`,
      url: editingRecord?.imageUrls?.[idx] || imagePath,
      existingPath: imagePath,
    })));
    imageCountRef.current = editingRecord?.images?.length || 0;

    form.setFieldsValue({
      clinic: editingRecord?.clinic?.id || editingRecord?.clinic?._id || editingRecord?.clinic || defaultClinic,
      airwayStatus: editingRecord?.airwayStatus,
      sleepStatus: editingRecord?.sleepStatus,
      alignment: editingRecord?.alignment,
      brushingHabit: editingRecord?.brushingHabit,
      reportSummary: editingRecord?.reportSummary,
      nextVisitDate: editingRecord?.nextVisitDate ? dayjs(editingRecord.nextVisitDate) : undefined,
      status: editingRecord?.status || 'draft',
      customObservations: editingRecord?.customObservations?.length ? editingRecord.customObservations : [],
      definitiveTreatment: editingRecord?.definitiveTreatment,
      semiPermanentTreatment: editingRecord?.semiPermanentTreatment,
      temporaryTreatment: editingRecord?.temporaryTreatment,
      preventiveTreatment: editingRecord?.preventiveTreatment,
    });
  }, [open, editingRecord, form, defaultClinic]);

  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

  const beforeImageUpload = (file) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      message.error('Only JPG or PNG images are allowed');
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      message.error('Each image must be smaller than 5MB');
      return Upload.LIST_IGNORE;
    }
    // Uses a ref (not imageList state) so simultaneous multi-file selections are checked
    // against a running count — state updates are batched/async and would otherwise let
    // every file in the same selection see the same stale count and all pass individually.
    if (imageCountRef.current >= MAX_IMAGES) {
      message.error(`You can upload up to ${MAX_IMAGES} images`);
      return Upload.LIST_IGNORE;
    }

    imageCountRef.current += 1;
    const thumbUrl = URL.createObjectURL(file);
    setImageList((prev) => [...prev, { uid: file.uid, name: file.name, originFileObj: file, url: thumbUrl }]);
    return Upload.LIST_IGNORE;
  };

  const handleRemoveImage = (uid) => {
    setImageList((prev) => {
      const removed = prev.find((f) => f.uid === uid);
      if (removed?.originFileObj && removed.url?.startsWith('blob:')) { URL.revokeObjectURL(removed.url); }
      return prev.filter((f) => f.uid !== uid);
    });
    imageCountRef.current = Math.max(0, imageCountRef.current - 1);
  };

  const handlePreviewImage = (file) => {
    setImagePreviewUrl(file.url);
    setImagePreviewOpen(true);
  };

  const handleImageDragStart = (idx) => () => setImageDragIndex(idx);
  const handleImageDragOver = (e) => e.preventDefault();
  const handleImageDrop = (idx) => (e) => {
    e.preventDefault();
    if (imageDragIndex === null || imageDragIndex === idx) return;
    setImageList((prev) => {
      const next = [...prev];
      const [moved] = next.splice(imageDragIndex, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setImageDragIndex(null);
  };

  const handleSubmit = async (rawValues) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('event', eventId);
      formData.append('child', childId);
      if (rawValues.airwayStatus) formData.append('airwayStatus', rawValues.airwayStatus);
      if (rawValues.sleepStatus) formData.append('sleepStatus', rawValues.sleepStatus);
      if (rawValues.alignment) formData.append('alignment', rawValues.alignment);
      if (rawValues.brushingHabit) formData.append('brushingHabit', rawValues.brushingHabit);
      if (rawValues.clinic) formData.append('clinic', rawValues.clinic);
      if (rawValues.reportSummary) formData.append('reportSummary', rawValues.reportSummary);
      if (rawValues.nextVisitDate) formData.append('nextVisitDate', rawValues.nextVisitDate.format('YYYY-MM-DD'));
      // Always sent (even empty) so clearing a selection is persisted, not silently ignored
      formData.append('definitiveTreatment', rawValues.definitiveTreatment || '');
      formData.append('semiPermanentTreatment', rawValues.semiPermanentTreatment || '');
      formData.append('temporaryTreatment', rawValues.temporaryTreatment || '');
      formData.append('preventiveTreatment', rawValues.preventiveTreatment || '');
      formData.append('status', rawValues.status || 'draft');

      const customObservations = (rawValues.customObservations || []).filter(
        (entry) => entry?.fieldName?.trim() && entry?.fieldValue?.trim()
      );
      formData.append('customObservations', JSON.stringify(customObservations));

      let newImageIdx = 0;
      const imageOrder = [];
      imageList.forEach((item) => {
        if (item.originFileObj) {
          imageOrder.push(`NEW_${newImageIdx}`);
          formData.append('images', item.originFileObj);
          newImageIdx += 1;
        } else {
          imageOrder.push(item.existingPath);
        }
      });
      formData.append('imageOrder', JSON.stringify(imageOrder));

      if (editingRecord) {
        await treatmentApi.update(editingRecord.id || editingRecord._id, formData);
        message.success('Treatment record updated successfully');
      } else {
        await treatmentApi.create(formData);
        message.success('Treatment record created successfully');
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      message.error(getApiErrorMessage(error) || 'Failed to save treatment record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, paddingRight: 20 }}>
          <div>
            <div style={{ fontWeight: 600 }}>Treatment Details</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{childName}</Text>
          </div>
          {editingRecord?.treatmentId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Treatment ID</Text>
              <Tag style={{ margin: 0, borderRadius: 9999, fontWeight: 600, background: 'var(--wl-primary-pale)', color: 'var(--wl-primary)', border: '1px solid var(--wl-primary-light)' }}>{editingRecord.treatmentId}</Tag>
            </div>
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
          <div style={{ marginBottom: 16, color: '#666', fontSize: 13 }}>
            Event: <Text strong>{eventName || '—'}</Text>
          </div>
          <Form.Item
            label="Clinic"
            name="clinic"
            rules={[{ required: true, message: 'Please select a clinic' }]}
          >
            <Select
              placeholder="Select clinic"
              loading={clinicsLoading}
              showSearch
              optionFilterProp="label"
              options={(clinicsData?.data || []).map((clinic) => ({
                value: clinic.id || clinic._id,
                label: clinic.name,
              }))}
            />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Observations</div>
            <Spin spinning={checkupLoading}>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Oral Hygiene Status">
                  {ORAL_HYGIENE_LABELS[checkupRecord?.oralHygieneStatus] || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Cavities">
                  {checkupRecord ? yesNo(checkupRecord.hasCavities) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Gum Issues">
                  {checkupRecord ? yesNo(checkupRecord.hasGumIssues) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Pain Reported">
                  {checkupRecord ? yesNo(checkupRecord.painReported) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Jaw Development">
                  {JAW_DEVELOPMENT_LABELS[checkupRecord?.jawDevelopment] || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Upper Jaw">
                  {JAW_WIDTH_LABELS[checkupRecord?.upperJaw] || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Lower Arch">
                  {JAW_WIDTH_LABELS[checkupRecord?.lowerArch] || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Tongue Movement">
                  {TONGUE_MOVEMENT_LABELS[checkupRecord?.tongueMovement] || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Mouth Breathing">
                  {checkupRecord ? yesNo(checkupRecord.mouthBreathing) : '—'}
                </Descriptions.Item>
              </Descriptions>
            </Spin>

            <div style={{ fontWeight: 700, margin: '16px 0 8px' }}>Custom Observations</div>
            <Form.List name="customObservations">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }} wrap>
                      <Form.Item
                        {...restField}
                        name={[name, 'fieldName']}
                        rules={[{ required: true, message: 'Field name is required' }]}
                        style={{ marginBottom: 0, width: 220 }}
                      >
                        <Input placeholder="Field name" style={{ borderColor: 'var(--wl-primary-light)' }} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'fieldValue']}
                        rules={[{ required: true, message: 'Field value is required' }]}
                        style={{ marginBottom: 0, width: 280 }}
                      >
                        <Input placeholder="Field value" style={{ borderColor: 'var(--wl-primary-light)' }} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                    </Space>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    style={{ borderColor: 'var(--wl-primary)', color: 'var(--wl-primary)' }}
                  >
                    Add Observation
                  </Button>
                </>
              )}
            </Form.List>
          </div>

          {/* <div style={{ padding: 16, background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 12, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 12 }}>Health Status</div>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label="Airway Status" name="airwayStatus" rules={[{ required: true, message: 'Airway status is required' }]}>
                  <Select placeholder="Select status" options={HEALTH_STATUS_OPTIONS} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Sleep Status" name="sleepStatus" rules={[{ required: true, message: 'Sleep status is required' }]}>
                  <Select placeholder="Select status" options={HEALTH_STATUS_OPTIONS} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Alignment" name="alignment" rules={[{ required: true, message: 'Alignment is required' }]}>
                  <Select placeholder="Select status" options={HEALTH_STATUS_OPTIONS} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Brushing Habit" name="brushingHabit" rules={[{ required: true, message: 'Brushing habit is required' }]}>
                  <Select placeholder="Select brushing habit" options={BRUSHING_HABIT_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>
          </div> */}

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Treatment Options</div>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label="Definitive Treatment" name="definitiveTreatment">
                  <Select placeholder="Select definitive treatment" allowClear options={toSelectOptions(DEFINITIVE_TREATMENT_OPTIONS)} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Semi-Permanent Treatment" name="semiPermanentTreatment">
                  <Select placeholder="Select semi-permanent treatment" allowClear options={toSelectOptions(SEMI_PERMANENT_TREATMENT_OPTIONS)} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Temporary Treatment" name="temporaryTreatment">
                  <Select placeholder="Select temporary treatment" allowClear options={toSelectOptions(TEMPORARY_TREATMENT_OPTIONS)} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Preventive Treatment" name="preventiveTreatment">
                  <Select placeholder="Select preventive treatment" allowClear options={toSelectOptions(PREVENTIVE_TREATMENT_OPTIONS)} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Treatment Images</div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              JPG or PNG only · Up to {MAX_IMAGES} images · Drag a thumbnail to reorder · Click to preview
            </Text>
            <Row gutter={[12, 12]}>
              {imageList.map((file, idx) => (
                <Col key={file.uid}>
                  <div
                    draggable
                    onDragStart={handleImageDragStart(idx)}
                    onDragOver={handleImageDragOver}
                    onDrop={handleImageDrop(idx)}
                    style={{
                      position: 'relative', width: 96, height: 96, borderRadius: 8, overflow: 'hidden',
                      border: '1px solid var(--wl-primary-pale, #f0f0f0)', cursor: 'grab',
                    }}
                  >
                    <img
                      src={file.url}
                      alt={file.name || `image-${idx}`}
                      onClick={() => handlePreviewImage(file)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                    />
                    <Button
                      shape="circle"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveImage(file.uid)}
                      style={{ position: 'absolute', top: 2, right: 2 }}
                    />
                  </div>
                </Col>
              ))}
              {imageList.length < MAX_IMAGES && (
                <Col>
                  <Upload showUploadList={false} beforeUpload={beforeImageUpload} multiple accept="image/jpeg,image/jpg,image/png">
                    <div style={{
                      width: 96, height: 96, display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', border: '1px dashed #d9d9d9', borderRadius: 8, cursor: 'pointer',
                    }}
                    >
                      <PlusOutlined />
                      <div style={{ marginTop: 8, fontSize: 12 }}>Select Images</div>
                    </div>
                  </Upload>
                </Col>
              )}
            </Row>
          </div>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span>Report Summary <Text type="secondary" style={{ fontWeight: 400 }}></Text></span>}
                name="reportSummary"
              >
                <Input.TextArea rows={3} placeholder="Summary of the uploaded report…" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Next Visit Date"
                name="nextVisitDate"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={(d) => d && d < dayjs().startOf('day')} placeholder="Select next visit date" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>Status</span>
            <Form.Item name="status" noStyle>
              <StatusToggle />
            </Form.Item>
          </div>

          <Row gutter={8} justify="end" style={{ marginTop: 8 }}>
            <Col>
              <Button onClick={onClose} disabled={loading}>Cancel</Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={loading} disabled={loading}>
                {editingRecord ? 'Update' : 'Create'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
      <Image
        style={{ display: 'none' }}
        preview={{
          visible: imagePreviewOpen,
          onVisibleChange: (visible) => setImagePreviewOpen(visible),
        }}
        src={imagePreviewUrl}
      />
    </Modal>
  );
};

export default TreatmentModal;
